import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`;
const GREETING = "Hi! I'm Brivio AI 👋 Ask me how Brivio works, what it costs, or what kind of local help you can get.";
const CHIPS = ["How does Brivio work?", "Is it free to use?", "What jobs can I get help with?", "How do I join?"];

export const WebsiteAssistant = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(API, { messages: next.map((m) => ({ role: m.role, content: m.content })) });
      setMessages((p) => [...p, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((p) => [...p, { role: "assistant", content: "Brivio AI is temporarily unavailable. You can sign up free and get started anytime!" }]);
    } finally { setSending(false); }
  };

  const scrollWaitlist = () => { setOpen(false); window.location.href = "/app/auth"; };

  return (
    <>
      <button
        data-testid="website-assistant-toggle"
        onClick={() => setOpen((o) => !o)}
        className="hidden sm:flex fixed bottom-20 right-5 z-[9999] items-center gap-2 rounded-full bg-[var(--b2-navy)] text-white pl-4 pr-5 py-3.5 font-semibold shadow-[0_12px_30px_rgba(13,27,61,0.35)] hover:bg-[var(--b2-navy-deep)] hover:scale-105 transition-all duration-300"
        aria-label="Open Brivio AI assistant"
      >
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        <span className="hidden sm:inline">{open ? "Close" : "Ask Brivio AI"}</span>
      </button>

      {open && (
        <div
          data-testid="website-assistant-panel"
          className="fixed bottom-44 right-5 z-[9999] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[540px] max-h-[75vh] bg-white rounded-3xl border border-[#E2E8F0] shadow-[0_30px_60px_rgba(0,0,0,0.18)] flex flex-col overflow-hidden"
        >
          <div className="flex items-center gap-3 px-5 py-4 bg-[var(--b2-navy)] text-white">
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white/20"><Sparkles className="w-5 h-5" /></div>
            <div>
              <p className="font-display font-bold leading-tight">Brivio AI</p>
              <p className="text-xs text-blue-100">Your friendly neighborhood helper</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]">
            {messages.map((m, i) => (
              <div key={i} data-testid={`assistant-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[var(--b2-green)] text-white rounded-br-sm" : "bg-white text-[#0F172A] border border-[#E2E8F0] rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start"><div className="bg-white border border-[#E2E8F0] rounded-2xl rounded-bl-sm px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-[#2563EB]" /></div></div>
            )}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {CHIPS.map((c) => (
                  <button key={c} data-testid="assistant-chip" onClick={() => send(c)} className="text-xs bg-white border border-[#E2E8F0] text-[var(--b2-green)] rounded-full px-3 py-1.5 hover:bg-[var(--b2-green-soft)] transition-colors">{c}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="px-3 pt-2 pb-1 bg-white border-t border-[#E2E8F0]">
            <button onClick={scrollWaitlist} data-testid="assistant-waitlist-cta" className="w-full mb-2 text-sm font-semibold text-[#22C55E] hover:text-[#16A34A]">Sign up free →</button>
            <div className="flex items-center gap-2 pb-2">
              <input
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask Brivio AI anything…"
                className="flex-1 rounded-full border border-[#E2E8F0] px-4 py-2.5 text-sm focus:outline-none focus:border-[#2563EB]"
              />
              <button data-testid="assistant-send" onClick={() => send()} disabled={sending} className="flex items-center justify-center w-10 h-10 rounded-full bg-[var(--b2-green)] text-white hover:bg-[var(--b2-green-hover)] disabled:opacity-50 transition-colors">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
